// SceneryGob — decorative props. Sim-relevant despite being eye candy: each occupies a
// gid (allocation order is hashed via 'ID'/'GNXT') and sits in the per-tile gid chains.
// No state machine, so Simulation::TrackState skips it. The TBitmap (sprite, baseline
// depth-sort key, clipping) is render-only; the sprite NAME is kept for completeness.
// game/GameObjects.cpp:727-833 + game/ht.h:6361-6388.

import type { SimContext } from '../context.ts';
import { Gob, kfGobLayerDepthSorted } from './gob.ts';
import { kgtScenery } from './gobtypes.ts';
import { WcFromTc } from '../math/coords.ts';
import { IniScanf } from '../data/ini.ts';

// The shipping client resolves scenery bitmap names through TexAtlasMgr::CreateTBitmap
// (texatlasmgr.cpp:346): lowercase, and the level-authored .tbm/.rbm becomes the atlas's
// .png — and SceneryGob::SaveState writes m_ptbm->GetFileName(), i.e. the RESOLVED key
// ("Plant4.tbm" saves as "plant4.png"). Mirror that so saves are byte-compatible.
function atlasKey(pszBitmap: string): string {
  let sz = '';
  for (let i = 0; i < pszBitmap.length; i++) {
    const c = pszBitmap.charCodeAt(i);
    sz += String.fromCharCode(c >= 0x41 && c <= 0x5a ? c + 0x20 : c);
  }
  return sz.replace(/\.(tbm|rbm)$/, '.png');
}

export class SceneryGob extends Gob {
  m_szBitmap = '';

  // game/GameObjects.cpp:733-736
  constructor(ctx: SimContext) {
    super(ctx);
  }

  // game/GameObjects.cpp:773-800 (knVerSceneryGobState 2) — saves the bitmap NAME
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    this.m_szBitmap = atlasKey(pstm.ReadString(29 /* kcbFilename */));
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    pstm.WriteString(this.m_szBitmap);
    return super.SaveState(pstm);
  }

  override GetType(): number {
    return kgtScenery;
  } // :794-797

  // game/GameObjects.cpp:742-753 — "%*d ,%s,%d ,%d"
  InitFromArgs(value: string): boolean {
    const { count, values } = IniScanf(value, '%*d ,%s,%d ,%d');
    if (count < 3) return false;
    const [szBitmap, tx, ty] = values as [string, number, number];
    return this.Init(WcFromTc(tx), WcFromTc(ty), 0, szBitmap);
  }

  // game/GameObjects.cpp:755-772 (CreateTBitmap is render; the name is retained)
  Init(wx: number, wy: number, ff: number, pszBitmap: string): boolean {
    this.m_wx = wx;
    this.m_wy = wy;
    this.m_szBitmap = atlasKey(pszBitmap);
    this.m_ff = (this.m_ff | ff | kfGobLayerDepthSorted) >>> 0;
    this.ctx.gobm.AddGob(this);
    return true;
  }
}

// self-register (see factory.ts)
import { registerGobType } from './factory.ts';
registerGobType(kgtScenery, (ctx) => new SceneryGob(ctx));
